import { cn } from '@/lib/utils/cn'
import { formatMs } from '@/lib/utils/format'

interface LatencyBarProps {
  /** Total wall time for the call, in milliseconds. */
  totalMs: number
  /** Time spent waiting on GitHub. The remainder is time spent in this server. */
  githubMs?: number | null
  /**
   * The largest value on the page. Bars are drawn against a SHARED scale so that a 40 ms
   * row and an 890 ms row are visibly different. Normalising each bar to its own row —
   * which most dashboards do — makes every bar full width and destroys the comparison the
   * bar exists to provide.
   */
  scaleMs: number
  showLabel?: boolean
  className?: string
}

/**
 * The recurring unit of this interface.
 *
 * Two segments: our time and GitHub's time. That split is the first question anyone asks
 * about a slow call, and answering it in the table means never opening a detail view to
 * find out.
 */
export function LatencyBar({
  totalMs,
  githubMs,
  scaleMs,
  showLabel = true,
  className,
}: LatencyBarProps) {
  const safeScale = Math.max(scaleMs, 1)
  const width = Math.min(100, Math.max((totalMs / safeScale) * 100, totalMs > 0 ? 1.5 : 0))
  const remote = githubMs ?? 0
  const remoteShare = totalMs > 0 ? Math.min(100, (remote / totalMs) * 100) : 0
  const localMs = Math.max(0, totalMs - remote)

  const title =
    remote > 0
      ? `${formatMs(totalMs)} total — ${formatMs(localMs)} server, ${formatMs(remote)} GitHub`
      : `${formatMs(totalMs)} total`

  return (
    <div className={cn('flex items-center gap-2.5', className)} title={title}>
      <div className="h-1.5 min-w-[3rem] flex-1 overflow-hidden rounded-sm bg-raised">
        <div
          className="flex h-full origin-left animate-grow"
          style={{ width: `${width}%` }}
          role="presentation"
        >
          <div className="h-full bg-primary-dim" style={{ width: `${100 - remoteShare}%` }} />
          <div className="h-full bg-remote" style={{ width: `${remoteShare}%` }} />
        </div>
      </div>
      {showLabel ? (
        <span className="mono w-16 shrink-0 text-right text-ink-dim">{formatMs(totalMs)}</span>
      ) : null}
    </div>
  )
}

/** Legend for the two segments. Rendered once per table, not once per row. */
export function LatencyLegend({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-4 text-micro text-ink-muted', className)}>
      <span className="flex items-center gap-1.5">
        <span className="h-1.5 w-4 rounded-sm bg-primary-dim" />
        server
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-1.5 w-4 rounded-sm bg-remote" />
        GitHub
      </span>
    </div>
  )
}
